import React from 'react';

import { useTranslation } from 'src/i18n/shim/useTranslation';
import styles from './SkipLink.module.scss';

export interface SkipLinkProps {
  /**
   * id of the element to move focus to (without the leading '#'). It must carry
   * `tabIndex={-1}` so the browser will actually focus it on activation.
   */
  readonly targetId: string;
  /** Visible link text once focused. Defaults to the localized 'Skip to main content'. */
  readonly children?: React.ReactNode;
}

/**
 * WCAG 2.4.1 (Bypass Blocks) skip link. It is the first focusable element on the
 * page and stays visually hidden until it receives keyboard focus, so the first
 * Tab press offers a jump straight to the main content — past the header and
 * navigation a keyboard or screen-reader user would otherwise traverse on every
 * page.
 *
 * Rendering a real anchor (rather than a button + scrollIntoView) means the
 * fragment lands in the address bar and native in-page navigation moves focus;
 * the `tabIndex={-1}` target is what lets focus, not just the scroll position,
 * follow the link.
 */
const SkipLink: React.FC<SkipLinkProps> = ({ targetId, children }) => {
  const { t } = useTranslation();

  return (
    <a href={`#${targetId}`} className={styles.skipLink}>
      {children ?? t('skip-link-main')}
    </a>
  );
};

export default SkipLink;
